/**
 * Date and currency formatting utilities
 *
 * Minimal subset needed for bookings types. Add more utilities as needed.
 */

/**
 * Format duration in minutes to human readable string
 *
 * @example
 * formatDuration(30)   // "30 min"
 * formatDuration(90)   // "1h 30min"
 * formatDuration(120)  // "2h"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}min`;
}

/**
 * Format currency amount
 *
 * @example
 * formatCurrency(150.50)                    // "R$ 150,50"
 * formatCurrency(150.50, 'USD', 'en-US')    // "$150.50"
 */
export function formatCurrency(
  amount: number,
  currency: string = 'BRL',
  locale: string = 'pt-BR'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format currency from cents.
 * Converts cents to main unit before formatting.
 *
 * @example
 * formatCurrencyFromCents(15050)  // "R$ 150,50"
 */
export function formatCurrencyFromCents(
  cents: number,
  currency: string = 'BRL',
  locale: string = 'pt-BR'
): string {
  return formatCurrency(cents / 100, currency, locale);
}
